import { Box } from '@mui/material'
import React from 'react'
import SaveStateIndicator, { SaveState } from './SaveStateIndicator'
import TextField from './TextField'
import Tooltip from './Tooltip'
import Txt from './Txt'

// Firma und Restaurant als ruhige Tabellen nebeneinander: links die Beschriftung, ein Klick darauf
// zeigt, wofür das Feld gebraucht wird; rechts das Eingabefeld ohne Rahmen. Jeder Abschnitt
// (eigene Felder, Adresse, Kontakt) ist ein eigenes Zeilenband, so stehen gleiche Abschnitte
// links und rechts auf derselben Höhe.

export type BaseDataField = { key: string; label: string; hint: string[] }

type BaseDataValues = Partial<Record<string, string>>

type ChangeHandler = (value: string | number, id?: string) => void

const sxColumns = {
	display: 'grid',
	gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	columnGap: '3rem',
	alignItems: 'start',
} as const

const sxRows = {
	display: 'grid',
	gridTemplateColumns: '9rem minmax(0, 1fr)',
	columnGap: '1rem',
	rowGap: '0.25rem',
	alignItems: 'center',
	minWidth: 0,
} as const

const sxHeader = { display: 'flex', alignItems: 'center', gap: '1rem' } as const

const sxGroupTitle = {
	gridColumn: '1 / -1',
	mt: '1rem',
	pb: '0.25rem',
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

const sxLabel = { cursor: 'help' } as const

// Ein Zeilenband: das erste Kind steht links (Firma), das zweite rechts (Restaurant).
export const BaseDataColumns = React.memo(
	({ children }: { children: React.ReactNode }): JSX.Element => <Box sx={sxColumns}>{children}</Box>
)
BaseDataColumns.displayName = 'BaseDataColumns'

export const BaseDataHeader = React.memo(
	({
		title,
		saveState,
		note,
	}: {
		title: string
		saveState: SaveState
		note?: string
	}): JSX.Element => (
		<Box minWidth={0}>
			<Box sx={sxHeader}>
				<Txt variant="subtitle1" fontWeight="bold">
					{title}
				</Txt>
				<SaveStateIndicator state={saveState} />
			</Box>
			{note ? (
				<Txt variant="body2" color="text.secondary" m="0.25rem 0 0 0">
					{note}
				</Txt>
			) : null}
		</Box>
	)
)
BaseDataHeader.displayName = 'BaseDataHeader'

// Die Zeilen eines Abschnitts, mit Überschrift für Adresse und Kontakt.
export const BaseDataRows = React.memo(
	({ title, children }: { title?: string; children?: React.ReactNode }): JSX.Element => (
		<Box sx={sxRows}>
			{title ? (
				<Box sx={sxGroupTitle}>
					<Txt variant="caption" color="text.secondary" fontWeight="bold">
						{title}
					</Txt>
				</Box>
			) : null}
			{children}
		</Box>
	)
)
BaseDataRows.displayName = 'BaseDataRows'

// Eine Zeile; columns teilt die rechte Zelle, wenn mehrere Felder darin stehen.
export const BaseDataRow = React.memo(
	({
		label,
		hint,
		columns,
		children,
	}: {
		label: string
		hint?: string[]
		columns?: string
		children: React.ReactNode
	}): JSX.Element => {
		const sxValue = React.useMemo(
			() => ({ display: 'grid', gridTemplateColumns: columns ?? '1fr', gap: '0.75rem' }),
			[columns]
		)

		return (
			<>
				<Tooltip text={hint}>
					<Txt variant="body2" color="text.secondary" sx={sxLabel}>
						{label}
					</Txt>
				</Tooltip>
				<Box sx={sxValue}>{children}</Box>
			</>
		)
	}
)
BaseDataRow.displayName = 'BaseDataRow'

// Ein Eingabefeld der Tabelle; der Platzhalter sagt in geteilten Zeilen, welches Feld welches ist.
export const BaseDataInput = React.memo(
	({
		field,
		values,
		onChange,
		placeHolder,
	}: {
		field: BaseDataField
		values: BaseDataValues
		onChange: ChangeHandler
		placeHolder?: string
	}): JSX.Element => (
		<TextField
			id={field.key}
			defaultValue={values[field.key] ?? ''}
			onChange={onChange}
			placeHolder={placeHolder}
			variant="standard"
			size="small"
			width="100%"
		/>
	)
)
BaseDataInput.displayName = 'BaseDataInput'

const fieldOf = (fields: BaseDataField[], key: string): BaseDataField =>
	fields.find((field) => field.key === key) ?? { key, label: key, hint: [] }

// Adresse bei Firma und Restaurant mit denselben Feldern: Straße und Nr., PLZ und Stadt.
export const BaseDataAddress = React.memo(
	({
		fields,
		values,
		onChange,
	}: {
		fields: BaseDataField[]
		values: BaseDataValues
		onChange: ChangeHandler
	}): JSX.Element => (
		<BaseDataRows title="Adresse">
			<BaseDataRow
				label="Straße, Nr."
				hint={fieldOf(fields, 'street').hint}
				columns="minmax(0, 1fr) 5rem"
			>
				<BaseDataInput
					field={fieldOf(fields, 'street')}
					values={values}
					onChange={onChange}
					placeHolder="Straße"
				/>
				<BaseDataInput
					field={fieldOf(fields, 'houseNumber')}
					values={values}
					onChange={onChange}
					placeHolder="Nr."
				/>
			</BaseDataRow>
			<BaseDataRow
				label="PLZ, Stadt"
				hint={fieldOf(fields, 'zipCode').hint}
				columns="5rem minmax(0, 1fr)"
			>
				<BaseDataInput
					field={fieldOf(fields, 'zipCode')}
					values={values}
					onChange={onChange}
					placeHolder="PLZ"
				/>
				<BaseDataInput
					field={fieldOf(fields, 'city')}
					values={values}
					onChange={onChange}
					placeHolder="Stadt"
				/>
			</BaseDataRow>
		</BaseDataRows>
	)
)
BaseDataAddress.displayName = 'BaseDataAddress'

// Kontakt bei Firma und Restaurant mit denselben Feldern: Telefon und E-Mail.
export const BaseDataContact = React.memo(
	({
		fields,
		values,
		onChange,
	}: {
		fields: BaseDataField[]
		values: BaseDataValues
		onChange: ChangeHandler
	}): JSX.Element => (
		<BaseDataRows title="Kontakt">
			<BaseDataRow label={fieldOf(fields, 'phone').label} hint={fieldOf(fields, 'phone').hint}>
				<BaseDataInput field={fieldOf(fields, 'phone')} values={values} onChange={onChange} />
			</BaseDataRow>
			<BaseDataRow label={fieldOf(fields, 'email').label} hint={fieldOf(fields, 'email').hint}>
				<BaseDataInput field={fieldOf(fields, 'email')} values={values} onChange={onChange} />
			</BaseDataRow>
		</BaseDataRows>
	)
)
BaseDataContact.displayName = 'BaseDataContact'
